import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material";
import { useMemo } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CheckList, CheckListItemType } from "../../../../components/check-list/CheckList";
import { ContentContainer } from "../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../components/content-section/ContentSection";
import LoadingZone from "../../../../components/loading-zone/LoadingZone";
import NoData from "../../../../components/no-data/NoData";
import SideSheetFilterInput from "../../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetMultiSelect from "../../../../components/side-sheet/side-sheet-multiselect/SideSheetMultiSelect";
import SideSheetSwitch from "../../../../components/side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../../../components/side-sheet/side-sheet/SideSheet";
import { StepMenuStepProps } from "../../../../components/step-menu/StepMenu";
import TabBar from "../../../../components/tab-bar/TabBar";
import TabContainer from "../../../../components/tab-container/TabContainer";
import TabWithCount from "../../../../components/tab-with-count/TabWithCount";
import { useRouteContext } from "../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { RoleSortEnum, useRoles } from "../../../../queries/user-role-administration/UseRoles";
import { UserSortEnum, useUsers } from "../../../../queries/user-role-administration/UseUsers";
import { createAvailableUserCheckListItems } from "../UICustomizationPresetEditorEditorUtils";
import { UICustomizationPresetAssociatedUserFieldType, UICustomizationPresetFieldType } from "../UICustomizationPresetEditorFieldTypes";
import { ALL_ROLES_OPTION_ID, allRolesOption, RoleOptionType, useMembershipStepState } from "./MembershipStepState";

export type MembershipStepProps = {
    organizationID: number | undefined;
} & StepMenuStepProps;

export default function MembershipStep({
    organizationID
}: MembershipStepProps) {

    const routeContext = useRouteContext();
    const { t } = useTranslation();


    const {
        state: {
            filterValue,
            viewSelected,
            selectedRoles
        },
        setFilterValue,
        setViewSelected,
        setSelectedRoles
    } = useMembershipStepState();

    const {
        data: users,
        status: usersStatus
    } = useQueryNotifications(useUsers)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        options: {
            filterText: filterValue,
            filterRoleIDs: selectedRoles?.map(r => r.id) ?? [],
            sortBy: UserSortEnum.LastNameAsc
        },
        enabled: organizationID !== undefined
    });

    const {
        data: roles,
        status: rolesStatus
    } = useQueryNotifications(useRoles)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        options: { filterText: filterValue, sortBy: RoleSortEnum.NameAsc },
        enabled: organizationID !== undefined
    });

    const {
        fields: selectedUsers,
        append: appendSelectedUser,
        remove: removeSelectedUser
    } = useFieldArray<UICustomizationPresetFieldType, "users", "fieldKey">({ name: "users", keyName: "fieldKey" });

    const availableUsers = useMemo(() => {
        return createAvailableUserCheckListItems(
            users?.data,
            selectedUsers
        );
    }, [
        users?.data,
        selectedUsers,
        selectedRoles
    ]);

    function onUserClicked(item: CheckListItemType) {
        updateUsers(item);
    }

    function updateUsers(item: CheckListItemType) {
        if (item.checked) {

            const user: UICustomizationPresetAssociatedUserFieldType = { id: Number(item.key) };

            appendSelectedUser(user, { shouldFocus: false });
        } else {

            // remove from array
            const userIndex = selectedUsers.findIndex(c => `${c.id}` === item.key);
            if (userIndex > -1) {
                removeSelectedUser(userIndex);
            }
        }
    }

    const userCount = availableUsers.filter(r => r.checked).length;
    const roleOptions = useMemo(() => {
        return [
            allRolesOption,
            ...(roles?.data?.roles.map(r => {
                return {
                    id: r.id,
                    name: r.name ?? ""
                } as RoleOptionType;
            }) ?? [])
        ];
    }, [
        roles?.data?.roles,
        t
    ]);

    function selectedRolesOnChange(
        _: React.SyntheticEvent,
        value: RoleOptionType[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<RoleOptionType> | undefined
    ) {
        const item = roleOptions.find(o => o.id === details?.option.id);

        if (item) {
            switch (reason) {
                case 'selectOption':
                    if (item.id === ALL_ROLES_OPTION_ID) {
                        setSelectedRoles(roleOptions.filter(o => o.id === ALL_ROLES_OPTION_ID));
                    } else {
                        setSelectedRoles(value.filter(role => role.id !== ALL_ROLES_OPTION_ID));
                    }
                    break;

                case 'removeOption':
                    setSelectedRoles(value.length === 0 ? [allRolesOption] : value);
                    break;

                default:
                    break;
            }
        }
    }

    return (
        <>
            <TabBar value={0} >
                <TabWithCount
                    label="Users"
                    count={userCount}
                    value={0}
                />
            </TabBar>

            <LoadingZone isLoading={usersStatus === "loading" || rolesStatus === "loading"}>
                <TabContainer visible={true}>
                    <ContentContainer>
                        <ContentSection>
                            {usersStatus === "success" &&
                                users?.filteredCount === 0 &&
                                <NoData
                                    title={t("uiCustomizationPresetEditor.steps.membership.noFilteredData")}
                                    text=""
                                />
                            }
                            <CheckList
                                items={availableUsers}
                                onItemClick={onUserClicked}
                                filter={
                                    (item: CheckListItemType) => {
                                        return !viewSelected || item.checked;
                                    }
                                }
                            />
                        </ContentSection>

                        <SideSheet
                            title={t("options")}
                            open={true}
                        >
                            <SideSheetFilterInput
                                label={t("uiCustomizationPresetEditor.steps.membership.filter")}
                                value={filterValue}
                                onChange={(value: string) => {
                                    setFilterValue(value);
                                }}
                            />

                            <SideSheetSwitch
                                label={t("uiCustomizationPresetEditor.steps.membership.viewSelected")}
                                checked={viewSelected}
                                onChange={(checked: boolean) => {
                                    setViewSelected(checked);
                                }}
                            />

                            <SideSheetMultiSelect<RoleOptionType>
                                label={t("uiCustomizationPresetEditor.steps.membership.filterByRoles")}
                                placeholder=""
                                options={roleOptions}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedRoles ?? []}
                                getOptionLabel={r => r.name}
                                shrinkLabel={true}
                                onChange={(event, value, reason, details) => {
                                    selectedRolesOnChange(event, value, reason, details);
                                }}
                            />
                        </SideSheet>
                    </ContentContainer>
                </TabContainer>
            </LoadingZone>
        </>
    );
}



